import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batchAddAssets } from '@/assetGroupEditor/assetGroup.actions';
import { useAssetGroupAssets } from '@/assetGroupEditor/assetGroup.store';
import { useLayoutAssetGroupEditorVisible } from '@/main/layout.store';
import { useTogglingSelectAll } from '@/search/hooks/useTogglingSelectAll.hook';
import { SelectAndAdd } from '@/search/multiSelect/SelectAndAdd.molecule';
import { ToggleSelectAll } from '@/search/multiSelect/ToggleSelectAll.molecule';
import { useSearchActions, useSearchSelectAllChecked, useSearchSelectedItemIds } from '@/search/search.store';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { batchAddItems } from '@/trendData/trend.actions';
import { isAsset, isDisplay, isTableDefinition } from '@/utilities/utilities';

interface TrendMultiSelectProps {
  items: any[];
}

/**
 * Manages trend multi-select
 */
export const TrendMultiSelect = ({ items }: TrendMultiSelectProps) => {
  const { t } = useTranslation();
  const { toggleSelectAll } = useSearchActions();
  const assetGroupEditorVisible = useLayoutAssetGroupEditorVisible();
  const selectAllChecked = useSearchSelectAllChecked();
  const selectedItemIds = useSearchSelectedItemIds();
  const assets = useAssetGroupAssets();

  const options = [
    { value: 'separate', label: t('MULTI_SELECT.TREND.SEPARATE') },
    { value: 'same', label: t('MULTI_SELECT.TREND.SAME') },
  ];

  const selectableItems = useMemo(
    () =>
      items?.filter(
        (item) =>
          (!isAsset(item) || assetGroupEditorVisible) &&
          !isDisplay(item) &&
          !isTableDefinition(item) &&
          !item.hasActiveChildren,
      ),
    [items, assetGroupEditorVisible],
  );

  const [isAssetAndItemSelected, isOnlyAssetsSelected] = useMemo(() => {
    if (!items.length || !selectedItemIds.length) return [false, null];

    const firstSelectedItem = items?.find((item) => item.id === selectedItemIds[0]);
    const curr = isAsset(firstSelectedItem) || firstSelectedItem.hasActiveChildren ? 1 : 0;
    for (let i = 0; i < selectedItemIds.length; i++) {
      const item = items?.find((item) => item.id === selectedItemIds[i]);
      const newCurr = isAsset(item) || item?.hasActiveChildren ? 1 : 0;
      if (curr !== newCurr) {
        return [true, null];
      }
    }

    return [false, curr === 1];
  }, [selectedItemIds, items]);

  const [selectActionOptions, setSelectActionOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (!assetGroupEditorVisible) return;
    switch (isOnlyAssetsSelected) {
      case true: {
        const options = [{ value: 'addAll', label: t('MULTI_SELECT.ASSET_GROUP.ADD_ALL') }];
        if (assets?.length > 0) {
          options.push({
            value: 'addMatching',
            label: t('MULTI_SELECT.ASSET_GROUP.ADD_MATCHING'),
          });
        }
        setSelectActionOptions(options);
        break;
      }

      case false:
        setSelectActionOptions([
          {
            value: 'addAll',
            label: t('MULTI_SELECT.ASSET_GROUP.ADD_ALL_ITEMS'),
          },
        ]);
        break;

      default:
        setSelectActionOptions([]);
        break;
    }
  }, [assets?.length, isOnlyAssetsSelected]);

  const { togglingSelectAll, setTogglingSelectAll } = useTogglingSelectAll({
    items: assetGroupEditorVisible ? items : selectableItems,
  });

  const showOnlyAssetGroupActions = selectableItems.length === 0;
  const displayOptions = showOnlyAssetGroupActions
    ? selectActionOptions
    : assetGroupEditorVisible
      ? [
          {
            label: t('MULTI_SELECT.TREND.HEADER'),
            options,
          },
          {
            label: t('MULTI_SELECT.ASSET_GROUP.HEADER'),
            options: selectActionOptions,
          },
        ]
      : options;

  const isSelectAllDisabled = selectableItems.length === 0;
  const [selectedValue, setSelectedValue] = useState(options[0]);

  useEffect(() => {
    const selected = !assetGroupEditorVisible
      ? options[0]
      : showOnlyAssetGroupActions
        ? selectActionOptions[0]
        : options[0];
    setSelectedValue(selected);
  }, [selectActionOptions]);

  return (
    <div className="flexColumnContainer tw:items-center" data-testid="multiSelectActions">
      <div className="tw:mr-sq-5">
        <ToggleSelectAll
          togglingSelectAll={togglingSelectAll}
          isChecked={Boolean(selectAllChecked)}
          onClick={() => {
            setTogglingSelectAll(true);
            toggleSelectAll(assetGroupEditorVisible ? items : selectableItems);
          }}
          isDisabled={isSelectAllDisabled}
        />
      </div>
      <SelectAndAdd
        selectedItemIds={selectedItemIds}
        options={displayOptions}
        onChange={setSelectedValue}
        selectedValue={selectedValue}
        onAddClick={() => {
          const selectedItems = selectedItemIds
            .map((id: string) => _.find(selectableItems, { id }))
            .filter((item: string) => !!item);
          if (_.get(selectedValue, 'value', '') === 'separate') {
            batchAddItems(selectedItems);
            doTrack(TRACK.MULTI_SELECT, 'Add items to separate lanes');
          } else if (_.get(selectedValue, 'value', '') === 'addAll') {
            batchAddAssets({ items, assetIds: selectedItemIds });

            doTrack(TRACK.MULTI_SELECT, 'Add all items or all items under assets');
          } else if (_.get(selectedValue, 'value', '') === 'addMatching') {
            batchAddAssets({
              items,
              assetIds: selectedItemIds,
              matchingOnly: true,
            });
            doTrack(TRACK.MULTI_SELECT, 'Add only matching items');
          } else {
            batchAddItems(selectedItems, false);
            doTrack(TRACK.MULTI_SELECT, 'Add items to same lane and axis');
          }
        }}
        isSelectAllDisabled={isSelectAllDisabled}
        isAssetAndItemSelected={isAssetAndItemSelected && assetGroupEditorVisible}
      />
    </div>
  );
};
